import React, { memo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { Chip } from 'src/components/shared/Chip'
import {
  paymentReleaseStatusDescriptions,
  paymentReleaseStatusLabels,
} from 'src/constants/invoice/constants'
import { PaymentReleaseStatus } from 'src/constants/invoice/enums'

interface Props {
  /** `null`/absent: no hold — renders a dash. */
  status?: PaymentReleaseStatus | null
  /** Shown beside the chip: the Central Time instant, or a count on a composed row. */
  detail: string | null
  dataTestId: string
}

const KNOWN_STATUSES: string[] = Object.values(PaymentReleaseStatus)

const chipStyles: Record<PaymentReleaseStatus, any> = {
  [PaymentReleaseStatus.Scheduled]: tw`bg-primary-100`,
  [PaymentReleaseStatus.Processing]: tw`bg-warning-100`,
  [PaymentReleaseStatus.Released]: tw`bg-success-200`,
  [PaymentReleaseStatus.Failed]: tw`bg-critical-50`,
}

const chipTextStyles: Record<PaymentReleaseStatus, any> = {
  [PaymentReleaseStatus.Scheduled]: tw`text-primary-700`,
  [PaymentReleaseStatus.Processing]: tw`text-warning-700`,
  [PaymentReleaseStatus.Released]: tw`text-success-500`,
  [PaymentReleaseStatus.Failed]: tw`text-critical-500`,
}

/**
 * The hold, visible on the same row as the money actions, so an admin about to run Process
 * payments sees it first. The chip's meaning is in its tooltip — `released` in particular does
 * not mean the payments settled, and `failed` needs a human.
 */
const PaymentReleaseCellComponent = ({ status, detail, dataTestId }: Props) => {
  if (!status) {
    return (
      <Typography variant="body2" css={tw`text-shades-500`} data-testid={dataTestId}>
        —
      </Typography>
    )
  }

  // A status the backend added after this client shipped: shown verbatim, never as an empty
  // chip or mistaken for "no hold".
  if (!KNOWN_STATUSES.includes(status)) {
    return (
      <Typography variant="body2" css={tw`truncate text-shades-700`} data-testid={dataTestId}>
        {status}
      </Typography>
    )
  }

  return (
    <div css={tw`flex min-w-0 flex-row items-center gap-2`} data-testid={dataTestId}>
      <Tooltip title={paymentReleaseStatusDescriptions[status]}>
        {/* Focusable so the explanation is reachable from the keyboard, not just on hover. */}
        <span tabIndex={0} css={tw`shrink-0`}>
          <Chip
            text={paymentReleaseStatusLabels[status]}
            className={chipStyles[status]}
            textClassName={chipTextStyles[status]}
            dataTestId={`${dataTestId}-chip`}
          />
        </span>
      </Tooltip>
      {detail ? (
        <Typography variant="body2" css={tw`truncate text-shades-700`}>
          {detail}
        </Typography>
      ) : null}
    </div>
  )
}

export const PaymentReleaseCell = memo(PaymentReleaseCellComponent)
